import { getColorwayWithTransparency, getSectionProps } from '@/utils/styles';
import { HomepageHeroProps } from 'libs/contentful/props';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import Media from '../shared/Media/Media';
import { cn } from '@/utils/styles';
import HomepageHeroList from './shared/HomepageHeroList';

const HomepageHeroCenteredVariant = ({
  sys,
  sectionName,
  headline,
  copy,
  images,
  color,
  transparentBackground,
}: HomepageHeroProps) => {
  // First image is the focal image, the rest display in the background
  const focalImage = images?.items?.[0];
  const backgroundImages = images?.items?.slice(1);

  // Image bundle settings used in css calculations
  const imageBundleHeight = '478px';
  const imageBundleMobileScale = 0.8;

  // Background image dimensions and styles by index
  const imageDimensions = [
    { width: '248px', height: '200px', className: 'mt-8' }, // 0: Top left image
    { width: '237px', height: '153px', className: '-mt-4 mr-10' }, // 1: Top right image
    { width: '182px', height: '141px', className: 'mt-8 ml-10' }, // 2: Bottom left image
    { width: '209px', height: '212px', className: '-mt-16' }, // 3: Bottom right image
  ];

  return (
    <div
      {...getSectionProps(
        undefined,
        undefined,
        sectionName,
        cn(
          getColorwayWithTransparency(color, transparentBackground),
          'max-sm:overflow-hidden max-sm:pb-4 max-sm:-mb-4', // Prevent vertical scroll on mobile while allowing overflow for shadow
        ),
      )}
      style={
        {
          '--image-bundle-height': imageBundleHeight,
          '--mobile-scale': imageBundleMobileScale,
          '--mobile-scale-percent': `${imageBundleMobileScale * 100}%`,
        } as React.CSSProperties
      }
      data-testid="homepage-hero-centered-variant">
      <div className="content-wrapper pt-5 sm:pt-4">
        <div className="flex flex-col gap-3 sm:gap-7">
          <div
            className="font-sans text-[36px] md:text-[68px] leading-[92%] sm:leading-[83%] font-medium sm:text-center"
            {...liveUpdateAttrs(sys?.id, 'headline')}>
            {headline && <RichTextHeadline headline={headline} level="h1" />}
          </div>
          <HomepageHeroList sys={sys} copy={copy} listDisplay="inline" />
          <div className="relative w-full max-sm:h-[calc(var(--image-bundle-height)*var(--mobile-scale))] mt-3 -mb-3 sm:mt-6 sm:-mb-6">
            <div className="max-sm:absolute-center mx-auto max-sm:scale-[var(--mobile-scale-percent)] w-[580px] h-[var(--image-bundle-height)]">
              {focalImage && (
                <div className="z-10 absolute-center">
                  <Media
                    key={focalImage.sys?.id}
                    media={{ ...focalImage }}
                    className="shadow-[25px_0_50px_rgba(0,0,0,0.5)]"
                    sizes="(max-width: 750px) 156px, 195px"
                  />
                </div>
              )}
              <div className="w-full flex flex-row flex-wrap justify-between items-start">
                {backgroundImages?.map((image, index) => (
                  <div
                    key={image.sys?.id + '-' + index}
                    style={{ width: imageDimensions[index]?.width, height: imageDimensions[index]?.height }}
                    className={imageDimensions[index]?.className}>
                    <Media media={{ ...image }} className="w-full h-full" sizes="(max-width: 750px) 200px, 250px" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomepageHeroCenteredVariant;
